import { supabase, toError } from '@/lib/supabase'
import type {
  Supplier,
  SupplierInsert,
  SupplierUpdate,
  SupplierContact,
  SupplierContactInsert,
  SupplierContactUpdate,
  SupplierWithContacts,
  SupplierStats,
  SupplierStatus,
  SupplierPerformanceTier,
  SupplierCorrespondence,
  SupplierCorrespondenceInsert,
  SupplierCorrespondenceUpdate,
  CorrespondenceWithContact,
  SupplierDocument,
  SupplierDocumentInsert,
  SupplierDocumentUpdate,
  SupplierDocumentWithDetails,
  SupplierDocumentCategory,
  SupplierProduct,
  SupplierProductInsert,
  SupplierProductUpdate,
  SupplierProductWithDetails,
  SupplierAudit,
  SupplierAuditInsert,
  SupplierAuditUpdate,
  SupplierAuditWithDocument,
  AuditResult,
  SupplierReview,
  SupplierReviewInsert,
  SupplierReviewUpdate,
  SupplierMetrics,
  SupplierMetricsInsert,
  SupplierWithMetrics,
} from '@/types/supplier'
import { profileService } from './profileService'
import { activityLogService } from './activityLogService'
import { captureError } from '@/lib/sentry'

export type SupplierWithUsers = Supplier & {
  creator?: { full_name: string | null } | null
  updater?: { full_name: string | null } | null
}

const SUPPLIER_SELECT = '*, creator:profiles!suppliers_created_by_fkey(full_name), updater:profiles!suppliers_updated_by_fkey(full_name)'

export const supplierService = {
  // ============================================
  // SUPPLIERS CRUD
  // ============================================

  /**
   * Get all suppliers for the current organization
   */
  async getSuppliers(): Promise<SupplierWithUsers[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('suppliers')
      .select(SUPPLIER_SELECT)
      .eq('organization_id', orgId)
      .order('name')

    if (error) {
      if (error.message?.includes('updated_by')) {
        const { data: fallback } = await supabase
          .from('suppliers')
          .select('*, creator:profiles!suppliers_created_by_fkey(full_name)')
          .eq('organization_id', orgId)
          .order('name')
        return fallback ?? []
      }
      console.error('Error fetching suppliers:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single supplier by ID
   */
  async getSupplier(id: string): Promise<SupplierWithUsers | null> {
    if (!id?.trim()) return null

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('suppliers')
      .select(SUPPLIER_SELECT)
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching supplier:', error)
      return null
    }

    return data
  },

  /**
   * Get a supplier with its contacts
   */
  async getSupplierWithContacts(id: string): Promise<SupplierWithContacts | null> {
    if (!id?.trim()) return null

    const supplier = await this.getSupplier(id)
    if (!supplier) return null

    const contacts = await this.getSupplierContacts(id)

    return {
      ...supplier,
      contacts,
    }
  },

  /**
   * Create a new supplier
   */
  async createSupplier(supplier: Omit<SupplierInsert, 'organization_id'>): Promise<Supplier | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('suppliers')
      .insert({
        ...supplier,
        organization_id: orgId,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating supplier:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'created',
        entityType: 'supplier',
        entityId: data.id,
        entityName: data.name,
      })
    }

    return data
  },

  /**
   * Update a supplier
   */
  async updateSupplier(id: string, updates: SupplierUpdate): Promise<Supplier | null> {
    if (!id?.trim()) throw new Error('Invalid supplier ID')

    const { data: { user } } = await supabase.auth.getUser()
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data, error } = await supabase
      .from('suppliers')
      .update({ ...updates, updated_by: user?.id })
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single()

    if (error) {
      console.error('Error updating supplier:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'updated',
        entityType: 'supplier',
        entityId: data.id,
        entityName: data.name,
        details: { fields_changed: Object.keys(updates) },
      })
    }

    return data
  },

  /**
   * Delete a supplier
   */
  async deleteSupplier(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid supplier ID')

    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')
    const existing = await this.getSupplier(id)

    const { error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error deleting supplier:', error)
      throw toError(error)
    }

    activityLogService.log({
      action: 'deleted',
      entityType: 'supplier',
      entityId: id,
      entityName: existing?.name,
    })

    return true
  },

  /**
   * Get suppliers by status
   */
  async getSuppliersByStatus(status: SupplierStatus): Promise<Supplier[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('organization_id', orgId)
      .eq('status', status)
      .order('name')

    if (error) {
      console.error('Error fetching suppliers by status:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get suppliers due for audit (next_audit_date is in the past or within 30 days)
   */
  async getSuppliersAuditDue(): Promise<Supplier[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)

    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('organization_id', orgId)
      .lte('next_audit_date', thirtyDaysFromNow.toISOString().split('T')[0])
      .order('next_audit_date')

    if (error) {
      console.error('Error fetching suppliers due for audit:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get supplier statistics for dashboard
   */
  async getSupplierStats(): Promise<SupplierStats> {
    const suppliers = await this.getSuppliers()

    const byStatus = suppliers.reduce((acc, supplier) => {
      acc[supplier.status] = (acc[supplier.status] || 0) + 1
      return acc
    }, {} as Record<SupplierStatus, number>)

    // Count suppliers with audit due
    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)
    const auditsDue = suppliers.filter((s) => {
      if (!s.next_audit_date) return false
      const auditDate = new Date(s.next_audit_date)
      return auditDate <= thirtyDaysFromNow
    }).length

    // Calculate average rating
    const suppliersWithRatings = suppliers.filter(
      (s) => s.rating_quality || s.rating_delivery || s.rating_responsiveness
    )
    let averageRating: number | null = null
    if (suppliersWithRatings.length > 0) {
      const totalRatings = suppliersWithRatings.reduce((sum, s) => {
        let count = 0
        let total = 0
        if (s.rating_quality) { total += s.rating_quality; count++ }
        if (s.rating_delivery) { total += s.rating_delivery; count++ }
        if (s.rating_responsiveness) { total += s.rating_responsiveness; count++ }
        return sum + (count > 0 ? total / count : 0)
      }, 0)
      averageRating = Math.round((totalRatings / suppliersWithRatings.length) * 10) / 10
    }

    return {
      total: suppliers.length,
      byStatus,
      auditsDue,
      averageRating,
    }
  },

  // ============================================
  // SUPPLIER CONTACTS CRUD
  // ============================================

  /**
   * Get all contacts for a supplier
   */
  async getSupplierContacts(supplierId: string): Promise<SupplierContact[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_contacts')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('is_primary', { ascending: false })
      .order('name')

    if (error) {
      console.error('Error fetching supplier contacts:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single contact by ID
   */
  async getContact(id: string): Promise<SupplierContact | null> {
    if (!id?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_contacts')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching contact:', error)
      return null
    }

    return data
  },

  /**
   * Create a new contact
   */
  async createContact(contact: SupplierContactInsert): Promise<SupplierContact | null> {
    const { data, error } = await supabase
      .from('supplier_contacts')
      .insert(contact)
      .select()
      .single()

    if (error) {
      console.error('Error creating contact:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Update a contact
   */
  async updateContact(id: string, updates: SupplierContactUpdate): Promise<SupplierContact | null> {
    if (!id?.trim()) throw new Error('Invalid contact ID')

    const { data, error } = await supabase
      .from('supplier_contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating contact:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Delete a contact
   */
  async deleteContact(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid contact ID')

    const { error } = await supabase
      .from('supplier_contacts')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting contact:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Set a contact as primary (automatically unsets others)
   */
  async setPrimaryContact(contactId: string): Promise<SupplierContact | null> {
    if (!contactId?.trim()) throw new Error('Invalid contact ID')

    const contact = await this.getContact(contactId)
    if (!contact) throw new Error('Contact not found')

    const { error } = await supabase
      .from('supplier_contacts')
      .update({ is_primary: false })
      .eq('supplier_id', contact.supplier_id)
      .neq('id', contactId)

    if (error) throw toError(error)

    return this.updateContact(contactId, { is_primary: true })
  },

  // ============================================
  // SUPPLIER CORRESPONDENCE CRUD
  // ============================================

  /**
   * Get all correspondence for a supplier
   */
  async getSupplierCorrespondence(supplierId: string): Promise<SupplierCorrespondence[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_correspondence')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching supplier correspondence:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get correspondence with contact details
   */
  async getCorrespondenceWithContacts(supplierId: string): Promise<CorrespondenceWithContact[]> {
    if (!supplierId?.trim()) return []

    const correspondence = await this.getSupplierCorrespondence(supplierId)
    const contacts = await this.getSupplierContacts(supplierId)

    // Create a map of contacts for quick lookup
    const contactMap = new Map(contacts.map((c) => [c.id, c]))

    return correspondence.map((item) => ({
      ...item,
      contact: item.contact_id ? contactMap.get(item.contact_id) || null : null,
    }))
  },

  /**
   * Get a single correspondence entry
   */
  async getCorrespondence(id: string): Promise<SupplierCorrespondence | null> {
    if (!id?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_correspondence')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching correspondence:', error)
      return null
    }

    return data
  },

  /**
   * Create a new correspondence entry
   */
  async createCorrespondence(
    correspondence: SupplierCorrespondenceInsert
  ): Promise<SupplierCorrespondence | null> {
    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('supplier_correspondence')
      .insert({
        ...correspondence,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating correspondence:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Update a correspondence entry
   */
  async updateCorrespondence(
    id: string,
    updates: SupplierCorrespondenceUpdate
  ): Promise<SupplierCorrespondence | null> {
    if (!id?.trim()) throw new Error('Invalid correspondence ID')

    const { data, error } = await supabase
      .from('supplier_correspondence')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating correspondence:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Delete a correspondence entry
   */
  async deleteCorrespondence(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid correspondence ID')

    const { error } = await supabase
      .from('supplier_correspondence')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting correspondence:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Get correspondence entries requiring follow-up
   */
  async getCorrespondenceRequiringFollowUp(supplierId?: string): Promise<SupplierCorrespondence[]> {
    let query = supabase
      .from('supplier_correspondence')
      .select('*')
      .eq('follow_up_required', true)
      .eq('follow_up_completed', false)
      .order('follow_up_date', { ascending: true })

    if (supplierId?.trim()) {
      query = query.eq('supplier_id', supplierId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching follow-up correspondence:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Mark a follow-up as completed
   */
  async completeFollowUp(id: string): Promise<SupplierCorrespondence | null> {
    return this.updateCorrespondence(id, { follow_up_completed: true })
  },

  // ============================================
  // SUPPLIER DOCUMENTS CRUD
  // ============================================

  /**
   * Get all documents linked to a supplier
   */
  async getSupplierDocuments(supplierId: string): Promise<SupplierDocument[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_documents')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('linked_at', { ascending: false })

    if (error) {
      console.error('Error fetching supplier documents:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get supplier documents with full document details
   */
  async getSupplierDocumentsWithDetails(supplierId: string): Promise<SupplierDocumentWithDetails[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_documents')
      .select(`
        *,
        document:documents(
          id,
          name,
          description,
          type,
          status,
          file_path,
          file_size,
          mime_type,
          expiry_date,
          issued_date,
          issuer
        )
      `)
      .eq('supplier_id', supplierId)
      .order('linked_at', { ascending: false })

    if (error) {
      console.error('Error fetching supplier documents with details:', error)
      return []
    }

    return (data ?? []) as SupplierDocumentWithDetails[]
  },

  /**
   * Link a document to a supplier
   */
  async linkDocument(data: SupplierDocumentInsert): Promise<SupplierDocument | null> {
    if (!data.supplier_id?.trim()) throw new Error('Invalid supplier ID')
    if (!data.document_id?.trim()) throw new Error('Invalid document ID')

    const { data: { user } } = await supabase.auth.getUser()

    const { data: result, error } = await supabase
      .from('supplier_documents')
      .insert({
        ...data,
        linked_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error linking document:', error)
      throw toError(error)
    }

    return result
  },

  /**
   * Update a supplier document link
   */
  async updateSupplierDocument(
    id: string,
    updates: SupplierDocumentUpdate
  ): Promise<SupplierDocument | null> {
    if (!id?.trim()) throw new Error('Invalid document link ID')

    const { data, error } = await supabase
      .from('supplier_documents')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating supplier document:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Unlink a document from a supplier
   */
  async unlinkDocument(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid document link ID')

    const { error } = await supabase
      .from('supplier_documents')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error unlinking document:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Get documents available for linking (not already linked to this supplier)
   * Optimized: parallel queries + minimal payload for linked IDs
   */
  async getAvailableDocuments(supplierId: string): Promise<{ id: string; name: string; type: string; expiry_date: string | null }[]> {
    if (!supplierId?.trim()) return []

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    // Fetch both queries in parallel for better performance
    const [linkedResult, documentsResult] = await Promise.all([
      // Only fetch document_id column to minimize payload
      supabase
        .from('supplier_documents')
        .select('document_id')
        .eq('supplier_id', supplierId),
      // Fetch all org documents
      supabase
        .from('documents')
        .select('id, name, type, expiry_date')
        .eq('organization_id', orgId)
        .order('name'),
    ])

    if (linkedResult.error) {
      console.error('Error fetching linked document IDs:', linkedResult.error)
      return []
    }

    if (documentsResult.error) {
      console.error('Error fetching available documents:', documentsResult.error)
      return []
    }

    // Create Set for O(1) lookup instead of O(n) array includes
    const linkedIds = new Set((linkedResult.data ?? []).map((d) => d.document_id))

    // Filter out already linked documents
    return (documentsResult.data ?? []).filter((doc) => !linkedIds.has(doc.id))
  },

  /**
   * Get supplier documents by category
   */
  async getSupplierDocumentsByCategory(
    supplierId: string,
    category: SupplierDocumentCategory
  ): Promise<SupplierDocumentWithDetails[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_documents')
      .select(`
        *,
        document:documents(
          id,
          name,
          description,
          type,
          status,
          file_path,
          file_size,
          mime_type,
          expiry_date,
          issued_date,
          issuer
        )
      `)
      .eq('supplier_id', supplierId)
      .eq('category', category)
      .order('linked_at', { ascending: false })

    if (error) {
      console.error('Error fetching supplier documents by category:', error)
      return []
    }

    return (data ?? []) as SupplierDocumentWithDetails[]
  },

  /**
   * Get documents expiring soon for a supplier
   */
  async getExpiringDocuments(supplierId: string, daysAhead = 30): Promise<SupplierDocumentWithDetails[]> {
    if (!supplierId?.trim()) return []

    const futureDate = new Date()
    futureDate.setDate(futureDate.getDate() + daysAhead)

    const { data, error } = await supabase
      .from('supplier_documents')
      .select(`
        *,
        document:documents(
          id,
          name,
          description,
          type,
          status,
          file_path,
          file_size,
          mime_type,
          expiry_date,
          issued_date,
          issuer
        )
      `)
      .eq('supplier_id', supplierId)
      .not('valid_until', 'is', null)
      .lte('valid_until', futureDate.toISOString().split('T')[0])
      .order('valid_until')

    if (error) {
      console.error('Error fetching expiring documents:', error)
      return []
    }

    return (data ?? []) as SupplierDocumentWithDetails[]
  },

  // ============================================
  // SUPPLIER PRODUCTS CRUD
  // ============================================

  /**
   * Get all products linked to a supplier
   */
  async getSupplierProducts(supplierId: string): Promise<SupplierProduct[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_products')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('is_primary_supplier', { ascending: false })
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching supplier products:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get supplier products with full product details
   */
  async getSupplierProductsWithDetails(supplierId: string): Promise<SupplierProductWithDetails[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_products')
      .select(`
        *,
        product:products(
          id,
          name,
          sku,
          description,
          category,
          status
        )
      `)
      .eq('supplier_id', supplierId)
      .order('is_primary_supplier', { ascending: false })
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching supplier products with details:', error)
      return []
    }

    return (data ?? []) as SupplierProductWithDetails[]
  },

  /**
   * Get all suppliers linked to a product (for product detail page)
   */
  async getProductSuppliers(productId: string): Promise<SupplierProductWithDetails[]> {
    if (!productId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_products')
      .select(`
        *,
        supplier:suppliers(
          id,
          name,
          code,
          status
        )
      `)
      .eq('product_id', productId)
      .order('is_primary_supplier', { ascending: false })
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching product suppliers:', error)
      return []
    }

    return (data ?? []) as SupplierProductWithDetails[]
  },

  /**
   * Get a single supplier product link by ID
   */
  async getSupplierProduct(id: string): Promise<SupplierProduct | null> {
    if (!id?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_products')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching supplier product:', error)
      return null
    }

    return data
  },

  /**
   * Link a product to a supplier
   */
  async linkProduct(data: SupplierProductInsert): Promise<SupplierProduct | null> {
    if (!data.supplier_id?.trim()) throw new Error('Invalid supplier ID')
    if (!data.product_id?.trim()) throw new Error('Invalid product ID')

    const { data: { user } } = await supabase.auth.getUser()

    const { data: result, error } = await supabase
      .from('supplier_products')
      .insert({
        ...data,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error linking product:', error)
      throw toError(error)
    }

    return result
  },

  /**
   * Update a supplier product link
   */
  async updateSupplierProduct(
    id: string,
    updates: SupplierProductUpdate
  ): Promise<SupplierProduct | null> {
    if (!id?.trim()) throw new Error('Invalid product link ID')

    const { data, error } = await supabase
      .from('supplier_products')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating supplier product:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Unlink a product from a supplier
   */
  async unlinkProduct(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid product link ID')

    const { error } = await supabase
      .from('supplier_products')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error unlinking product:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Get products available for linking (not already linked to this supplier)
   */
  async getAvailableProducts(supplierId: string): Promise<{ id: string; name: string; sku: string | null; category: string | null; status: string }[]> {
    if (!supplierId?.trim()) return []

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    // Fetch both queries in parallel for better performance
    const [linkedResult, productsResult] = await Promise.all([
      // Only fetch product_id column to minimize payload
      supabase
        .from('supplier_products')
        .select('product_id')
        .eq('supplier_id', supplierId),
      // Fetch all org products
      supabase
        .from('products')
        .select('id, name, sku, category, status')
        .eq('organization_id', orgId)
        .order('name'),
    ])

    if (linkedResult.error) {
      console.error('Error fetching linked product IDs:', linkedResult.error)
      return []
    }

    if (productsResult.error) {
      console.error('Error fetching available products:', productsResult.error)
      return []
    }

    // Create Set for O(1) lookup instead of O(n) array includes
    const linkedIds = new Set((linkedResult.data ?? []).map((d) => d.product_id))

    // Filter out already linked products
    return (productsResult.data ?? []).filter((product) => !linkedIds.has(product.id))
  },

  /**
   * Set a supplier as primary for a product
   */
  async setPrimarySupplier(id: string): Promise<SupplierProduct | null> {
    if (!id?.trim()) throw new Error('Invalid product link ID')

    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    // First, get the supplier product to find the product_id
    const supplierProduct = await this.getSupplierProduct(id)
    if (!supplierProduct) throw new Error('Supplier product link not found')

    // Atomically unset all other primaries and set this one, scoped to org
    const { error: unsetError } = await supabase
      .from('supplier_products')
      .update({ is_primary_supplier: false })
      .eq('product_id', supplierProduct.product_id)
      .neq('id', id)

    if (unsetError) {
      console.error('Error unsetting primary suppliers:', unsetError)
      captureError(unsetError, { operation: 'setPrimarySupplier', supplier_product_id: id })
      throw toError(unsetError)
    }

    // Set this one as primary
    return this.updateSupplierProduct(id, { is_primary_supplier: true })
  },

  /**
   * Get approved suppliers for a product
   */
  async getApprovedSuppliersForProduct(productId: string): Promise<SupplierProductWithDetails[]> {
    if (!productId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_products')
      .select(`
        *,
        supplier:suppliers(
          id,
          name,
          code,
          status
        )
      `)
      .eq('product_id', productId)
      .eq('approved_for_production', true)
      .order('is_primary_supplier', { ascending: false })

    if (error) {
      console.error('Error fetching approved suppliers:', error)
      return []
    }

    return (data ?? []) as SupplierProductWithDetails[]
  },

  // ============================================
  // SUPPLIER AUDITS CRUD
  // ============================================

  /**
   * Get all audits for a supplier
   */
  async getSupplierAudits(supplierId: string): Promise<SupplierAudit[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_audits')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('audit_date', { ascending: false })

    if (error) {
      console.error('Error fetching supplier audits:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get supplier audits with linked document details
   */
  async getSupplierAuditsWithDocuments(supplierId: string): Promise<SupplierAuditWithDocument[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_audits')
      .select(`
        *,
        report_document:documents(
          id,
          name,
          file_path
        )
      `)
      .eq('supplier_id', supplierId)
      .order('audit_date', { ascending: false })

    if (error) {
      console.error('Error fetching supplier audits with documents:', error)
      return []
    }

    return (data ?? []) as SupplierAuditWithDocument[]
  },

  /**
   * Get a single audit by ID
   */
  async getAudit(id: string): Promise<SupplierAudit | null> {
    if (!id?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_audits')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching audit:', error)
      return null
    }

    return data
  },

  /**
   * Get a single audit with document details
   */
  async getAuditWithDocument(id: string): Promise<SupplierAuditWithDocument | null> {
    if (!id?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_audits')
      .select(`
        *,
        report_document:documents(
          id,
          name,
          file_path
        )
      `)
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching audit with document:', error)
      return null
    }

    return data as SupplierAuditWithDocument
  },

  /**
   * Create a new audit
   */
  async createAudit(audit: SupplierAuditInsert): Promise<SupplierAudit | null> {
    if (!audit.supplier_id?.trim()) throw new Error('Invalid supplier ID')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('supplier_audits')
      .insert({
        ...audit,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating audit:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Update an audit
   */
  async updateAudit(id: string, updates: SupplierAuditUpdate): Promise<SupplierAudit | null> {
    if (!id?.trim()) throw new Error('Invalid audit ID')

    const { data, error } = await supabase
      .from('supplier_audits')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating audit:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Delete an audit
   */
  async deleteAudit(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid audit ID')

    const { error } = await supabase
      .from('supplier_audits')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting audit:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Get audits requiring follow-up
   */
  async getAuditsRequiringFollowUp(supplierId?: string): Promise<SupplierAudit[]> {
    let query = supabase
      .from('supplier_audits')
      .select('*')
      .eq('follow_up_required', true)
      .order('next_audit_due', { ascending: true })

    if (supplierId?.trim()) {
      query = query.eq('supplier_id', supplierId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching audits requiring follow-up:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get audits by result
   */
  async getAuditsByResult(result: AuditResult, supplierId?: string): Promise<SupplierAudit[]> {
    let query = supabase
      .from('supplier_audits')
      .select('*')
      .eq('result', result)
      .order('audit_date', { ascending: false })

    if (supplierId?.trim()) {
      query = query.eq('supplier_id', supplierId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching audits by result:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get upcoming audits (for dashboard)
   */
  async getUpcomingAudits(daysAhead = 30): Promise<SupplierAuditWithDocument[]> {
    const futureDate = new Date()
    futureDate.setDate(futureDate.getDate() + daysAhead)

    const { data, error } = await supabase
      .from('supplier_audits')
      .select(`
        *,
        report_document:documents(
          id,
          name,
          file_path
        )
      `)
      .not('next_audit_due', 'is', null)
      .lte('next_audit_due', futureDate.toISOString().split('T')[0])
      .order('next_audit_due', { ascending: true })

    if (error) {
      console.error('Error fetching upcoming audits:', error)
      return []
    }

    return (data ?? []) as SupplierAuditWithDocument[]
  },

  /**
   * Get the most recent audit for a supplier
   */
  async getLatestAudit(supplierId: string): Promise<SupplierAudit | null> {
    if (!supplierId?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_audits')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('audit_date', { ascending: false })
      .limit(1)
      .single()

    if (error) {
      // Not an error if no audits exist
      if (error.code === 'PGRST116') return null
      console.error('Error fetching latest audit:', error)
      return null
    }

    return data
  },

  /**
   * Get audit statistics for a supplier
   */
  async getAuditStats(supplierId: string): Promise<{
    total: number
    byResult: Record<AuditResult, number>
    averageScore: number | null
    openCorrectiveActions: number
    lastAuditDate: string | null
    nextAuditDue: string | null
  }> {
    const audits = await this.getSupplierAudits(supplierId)

    const byResult = audits.reduce((acc, audit) => {
      acc[audit.result] = (acc[audit.result] || 0) + 1
      return acc
    }, {} as Record<AuditResult, number>)

    // Calculate average score
    const auditsWithScores = audits.filter((a) => a.score !== null)
    const averageScore = auditsWithScores.length > 0
      ? Math.round(auditsWithScores.reduce((sum, a) => sum + (a.score ?? 0), 0) / auditsWithScores.length)
      : null

    // Count open corrective actions across all audits
    const openCorrectiveActions = audits.reduce((count, audit) => {
      const openActions = (audit.corrective_actions || []).filter(
        (action) => ['open', 'in_progress', 'overdue'].includes(action.status)
      )
      return count + openActions.length
    }, 0)

    // Get last audit date and next due date
    const sortedByDate = [...audits].sort(
      (a, b) => new Date(b.audit_date).getTime() - new Date(a.audit_date).getTime()
    )
    const lastAuditDate = sortedByDate.length > 0 ? sortedByDate[0].audit_date : null

    // Find the next audit due from all audits
    const auditsWithNextDue = audits.filter((a) => a.next_audit_due)
    const sortedByNextDue = auditsWithNextDue.sort(
      (a, b) => new Date(a.next_audit_due!).getTime() - new Date(b.next_audit_due!).getTime()
    )
    const nextAuditDue = sortedByNextDue.length > 0 ? sortedByNextDue[0].next_audit_due : null

    return {
      total: audits.length,
      byResult,
      averageScore,
      openCorrectiveActions,
      lastAuditDate,
      nextAuditDue,
    }
  },

  // ============================================
  // SUPPLIER REVIEWS CRUD
  // ============================================

  /**
   * Get all reviews for a supplier
   */
  async getSupplierReviews(supplierId: string): Promise<SupplierReview[]> {
    if (!supplierId?.trim()) return []

    const { data, error } = await supabase
      .from('supplier_reviews')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('review_date', { ascending: false })

    if (error) {
      console.error('Error fetching supplier reviews:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single review by ID
   */
  async getReview(id: string): Promise<SupplierReview | null> {
    if (!id?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_reviews')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching review:', error)
      return null
    }

    return data
  },

  /**
   * Create a new review
   */
  async createReview(review: SupplierReviewInsert): Promise<SupplierReview | null> {
    if (!review.supplier_id?.trim()) throw new Error('Invalid supplier ID')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('supplier_reviews')
      .insert({
        ...review,
        reviewed_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating review:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Update a review
   */
  async updateReview(id: string, updates: SupplierReviewUpdate): Promise<SupplierReview | null> {
    if (!id?.trim()) throw new Error('Invalid review ID')

    const { data, error } = await supabase
      .from('supplier_reviews')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating review:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Delete a review
   */
  async deleteReview(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid review ID')

    const { error } = await supabase
      .from('supplier_reviews')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting review:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Get the latest review for a supplier
   */
  async getLatestReview(supplierId: string): Promise<SupplierReview | null> {
    if (!supplierId?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_reviews')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('review_date', { ascending: false })
      .limit(1)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Error fetching latest review:', error)
      return null
    }

    return data
  },

  // ============================================
  // SUPPLIER METRICS
  // ============================================

  /**
   * Get metrics for a supplier
   */
  async getSupplierMetrics(supplierId: string): Promise<SupplierMetrics | null> {
    if (!supplierId?.trim()) return null

    const { data, error } = await supabase
      .from('supplier_metrics')
      .select('*')
      .eq('supplier_id', supplierId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Error fetching supplier metrics:', error)
      return null
    }

    return data
  },

  /**
   * Create or update metrics for a supplier
   */
  async upsertSupplierMetrics(
    supplierId: string,
    metrics: Omit<SupplierMetricsInsert, 'supplier_id'>
  ): Promise<SupplierMetrics | null> {
    if (!supplierId?.trim()) throw new Error('Invalid supplier ID')

    const { data, error } = await supabase
      .from('supplier_metrics')
      .upsert(
        {
          ...metrics,
          supplier_id: supplierId,
          metrics_updated_at: new Date().toISOString(),
        },
        { onConflict: 'supplier_id' }
      )
      .select()
      .single()

    if (error) {
      console.error('Error upserting supplier metrics:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Calculate and update metrics from correspondence count
   */
  async updateMetricsFromCorrespondence(supplierId: string): Promise<void> {
    if (!supplierId?.trim()) return

    const correspondence = await this.getSupplierCorrespondence(supplierId)
    const pendingFollowUps = correspondence.filter(
      (c) => c.follow_up_required && !c.follow_up_completed
    ).length

    await this.upsertSupplierMetrics(supplierId, {
      total_correspondence: correspondence.length,
      pending_follow_ups: pendingFollowUps,
    })
  },

  // ============================================
  // SUPPLIER COMPARISON
  // ============================================

  /**
   * Get multiple suppliers with their metrics for comparison
   */
  async getSuppliersForComparison(supplierIds: string[]): Promise<SupplierWithMetrics[]> {
    if (!supplierIds.length) return []

    const suppliers = await Promise.all(
      supplierIds.map((id) => this.getSupplier(id))
    )

    const metrics = await Promise.all(
      supplierIds.map((id) => this.getSupplierMetrics(id))
    )

    const metricsMap = new Map(supplierIds.map((id, i) => [id, metrics[i]]))
    return suppliers
      .filter((s): s is Supplier => s !== null)
      .map((supplier) => ({
        ...supplier,
        metrics: metricsMap.get(supplier.id) ?? null,
      }))
  },

  /**
   * Get all suppliers with metrics for dashboard
   */
  async getAllSuppliersWithMetrics(): Promise<SupplierWithMetrics[]> {
    const suppliers = await this.getSuppliers()
    
    const metricsPromises = suppliers.map((s) => this.getSupplierMetrics(s.id))
    const allMetrics = await Promise.all(metricsPromises)

    return suppliers.map((supplier, index) => ({
      ...supplier,
      metrics: allMetrics[index],
    }))
  },

  /**
   * Get suppliers by performance tier
   */
  async getSuppliersByPerformanceTier(tier: SupplierPerformanceTier): Promise<Supplier[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('organization_id', orgId)
      .eq('performance_tier', tier)
      .order('overall_score', { ascending: false })

    if (error) {
      console.error('Error fetching suppliers by performance tier:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get top performing suppliers
   */
  async getTopSuppliers(limit = 5): Promise<Supplier[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('organization_id', orgId)
      .not('overall_score', 'is', null)
      .order('overall_score', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Error fetching top suppliers:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get suppliers needing attention (poor performers or under review)
   */
  async getSuppliersNeedingAttention(): Promise<Supplier[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('organization_id', orgId)
      .in('performance_tier', ['poor', 'under_review'])
      .order('overall_score', { ascending: true })

    if (error) {
      console.error('Error fetching suppliers needing attention:', error)
      return []
    }

    return data ?? []
  },
}
